function makeColorBufferTexture(
  gl,
  internal_format,
  format,
  ty,
  filter,
  attachment
) {
  const texture = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, texture);

  gl.texImage2D(
    gl.TEXTURE_2D, // target
    0, // level
    internal_format, // internal_format
    gl.canvas.width, // width
    gl.canvas.height, // height
    0, // border
    format, // format
    ty, // ty
    null // pixels
  );

  // Disable mipmaps
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_BASE_LEVEL, 0);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAX_LEVEL, 0);

  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);

  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

  gl.framebufferTexture2D(
    gl.DRAW_FRAMEBUFFER, // target
    attachment, // attachment
    gl.TEXTURE_2D, // texture_target
    texture, // texture
    0 // level
  );

  gl.bindTexture(gl.TEXTURE_2D, null);

  return texture;
}

function makeRenderbuffer(gl, samples, internal_format, attachment) {
  const renderbuffer = gl.createRenderbuffer();
  gl.bindRenderbuffer(gl.RENDERBUFFER, renderbuffer);

  if (samples > 1) {
    gl.renderbufferStorageMultisample(
      gl.RENDERBUFFER,
      samples,
      internal_format,
      gl.canvas.width,
      gl.canvas.height
    );
  } else {
    gl.renderbufferStorage(
      gl.RENDERBUFFER,
      internal_format,
      gl.canvas.width,
      gl.canvas.height
    );
  }

  gl.framebufferRenderbuffer(
    gl.FRAMEBUFFER, // target
    attachment, // attachment
    gl.RENDERBUFFER, // renderbuffer_target
    renderbuffer // renderbuffer
  );

  gl.bindRenderbuffer(gl.RENDERBUFFER, null);

  return renderbuffer;
}

export default class Gbuffer {
  constructor(gl, samples = 0) {
    this.samples = samples;

    this.renderFbo = gl.createFramebuffer();
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this.renderFbo);

    this.renderAlbedo = makeRenderbuffer(
      gl,
      this.samples,
      gl.RGBA8,
      gl.COLOR_ATTACHMENT0
    );
    this.renderPosition = makeRenderbuffer(
      gl,
      this.samples,
      gl.RGBA32F,
      gl.COLOR_ATTACHMENT1
    );
    this.renderNormal = makeRenderbuffer(
      gl,
      this.samples,
      gl.RGBA16F,
      gl.COLOR_ATTACHMENT2
    );
    this.renderDepth = makeRenderbuffer(
      gl,
      this.samples,
      gl.DEPTH24_STENCIL8,
      gl.DEPTH_STENCIL_ATTACHMENT
    );

    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);

    this.outputFbo = gl.createFramebuffer();
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this.outputFbo);

    this.outputAlbedo = makeColorBufferTexture(
      gl,
      gl.RGBA8, // internal format
      gl.RGBA, // format
      gl.UNSIGNED_BYTE, // type
      gl.NEAREST, // filter
      gl.COLOR_ATTACHMENT0 // attachment
    );
    this.outputPosition = makeColorBufferTexture(
      gl,
      gl.RGBA32F, // internal format
      gl.RGBA, // format
      gl.FLOAT, // type
      gl.NEAREST, // filter
      gl.COLOR_ATTACHMENT1 // attachment
    );
    this.outputNormal = makeColorBufferTexture(
      gl,
      gl.RGBA16F, // internal format
      gl.RGBA, // format
      gl.FLOAT, // type
      gl.NEAREST, // filter
      gl.COLOR_ATTACHMENT2 // attachment
    );

    this.outputDepth = makeRenderbuffer(
      gl,
      0, // samples
      gl.DEPTH24_STENCIL8, // internal format
      gl.DEPTH_STENCIL_ATTACHMENT // attachment
    );

    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);
  }

  initPass(gl) {
    // When multi-sampling is disabled, we can draw directly in the output framebuffer.
    if (this.samples <= 1) {
      gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this.outputFbo);
    } else {
      gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this.renderFbo);
    }

    gl.drawBuffers([
      gl.COLOR_ATTACHMENT0,
      gl.COLOR_ATTACHMENT1,
      gl.COLOR_ATTACHMENT2,
    ]);

    gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);
    gl.clearColor(0.0, 0.0, 0.0, 0.0);
    gl.clearDepth(1.0);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

    // gl.enable(gl.CULL_FACE);
    // gl.cullFace(gl.BACK);

    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LEQUAL);

    gl.disable(gl.BLEND);
  }

  commit(gl) {
    // Without multi-sampling, we draw directly in the output framebuffer,
    // so there's no need to blit the render fbo into the output one.
    if (this.samples <= 1) {
      gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);
      return;
    }

    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, this.renderFbo);
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this.outputFbo);

    // Blit albedo & depth
    gl.readBuffer(gl.COLOR_ATTACHMENT0);
    gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.NONE, gl.NONE]);

    // prettier-ignore
    gl.blitFramebuffer(
      0, 0, gl.canvas.width, gl.canvas.height,
      0, 0, gl.canvas.width, gl.canvas.height,
      gl.COLOR_BUFFER_BIT, gl.LINEAR
    );

    // prettier-ignore
    gl.blitFramebuffer(
      0, 0, gl.canvas.width, gl.canvas.height,
      0, 0, gl.canvas.width, gl.canvas.height,
      gl.DEPTH_BUFFER_BIT, gl.NEAREST,
    );

    // Blit positions
    gl.readBuffer(gl.COLOR_ATTACHMENT1);
    gl.drawBuffers([gl.NONE, gl.COLOR_ATTACHMENT1, gl.NONE]);

    // prettier-ignore
    gl.blitFramebuffer(
      0, 0, gl.canvas.width, gl.canvas.height,
      0, 0, gl.canvas.width, gl.canvas.height,
      gl.COLOR_BUFFER_BIT, gl.LINEAR
    );

    // Blit normals
    gl.readBuffer(gl.COLOR_ATTACHMENT2);
    gl.drawBuffers([gl.NONE, gl.NONE, gl.COLOR_ATTACHMENT2]);

    // prettier-ignore
    gl.blitFramebuffer(
      0, 0, gl.canvas.width, gl.canvas.height,
      0, 0, gl.canvas.width, gl.canvas.height,
      gl.COLOR_BUFFER_BIT, gl.LINEAR
    );

    gl.invalidateFramebuffer(gl.READ_FRAMEBUFFER, [
      gl.COLOR_ATTACHMENT0,
      gl.COLOR_ATTACHMENT1,
      gl.COLOR_ATTACHMENT2,
      gl.COLOR_ATTACHMENT3,
    ]);

    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null);
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);
  }

  cleanup(gl) {
    gl.deleteRenderbuffer(this.renderAlbedo);
    gl.deleteRenderbuffer(this.renderPosition);
    gl.deleteRenderbuffer(this.renderNormal);
    gl.deleteRenderbuffer(this.renderDepth);

    gl.deleteTexture(this.outputAlbedo);
    gl.deleteTexture(this.outputPosition);
    gl.deleteTexture(this.outputNormal);
    gl.deleteRenderbuffer(this.outputDepth);

    gl.deleteFramebuffer(this.renderFbo);
    gl.deleteFramebuffer(this.outputFbo);
  }
}
